import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

// 設定UIで共通利用する「表面の強調スタイル」。デザイン言語の単一情報源。
//  - variant='master': プライマリで全面強調（親設定向け）
//  - variant='default': 白系の控えめ強調（プライマリはアイコン円とコントロールのみに留める）
export type SettingVariant = 'master' | 'default';

const surfaceSx = (
  checked: boolean,
  variant: SettingVariant,
  mb: number = 1.5,
): SystemStyleObject<Theme> => {
  const isMaster = variant === 'master';
  return {
    borderRadius: 2,
    mb,
    overflow: 'hidden',
    transition: 'border-color .25s ease, background-color .25s ease, box-shadow .25s ease',
    borderWidth: checked && isMaster ? 1 : 1,
    borderColor: checked
      ? (isMaster ? 'primary.main' : (theme: Theme) => alpha(theme.palette.common.white, 0.22))
      : 'divider',
    bgcolor: (theme: Theme) =>
      checked
        ? (isMaster
            ? alpha(theme.palette.primary.main, 0.06)
            : alpha(theme.palette.common.white, 0.06))
        : 'background.paper',
    boxShadow: (theme: Theme) =>
      checked && isMaster ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.22)}` : 'none',
  };
};

// アイコン円（共通）。checked時はプライマリ発色。
const IconBadge: React.FC<{ checked: boolean; size: number; children: React.ReactNode }> = ({
  checked, size, children,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: '50%',
      transition: 'all .25s ease',
      color: checked ? 'primary.main' : 'text.disabled',
      bgcolor: (theme: Theme) =>
        checked ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.text.primary, 0.06),
    }}
  >
    {children}
  </Box>
);

// =============================================================
// トグルカード（1カード = 1つの真偽値）。カード全体クリックでON/OFF。
// =============================================================
export interface SettingToggleCardProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  disabled?: boolean;
  variant?: SettingVariant;
  accentBadge?: React.ReactNode;
  children?: React.ReactNode;
}

export const SettingToggleCard: React.FC<SettingToggleCardProps> = ({
  icon, title, description, checked, onToggle, disabled = false,
  variant = 'default', accentBadge, children,
}) => {
  const isMaster = variant === 'master';
  return (
    <Paper elevation={0} variant="outlined" sx={surfaceSx(checked, variant)}>
      <Box
        onClick={() => !disabled && onToggle(!checked)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: isMaster ? 2 : 1.5,
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        {icon && <IconBadge checked={checked} size={isMaster ? 44 : 38}>{icon}</IconBadge>}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant={isMaster ? 'subtitle2' : 'body2'} sx={{ fontWeight: isMaster ? 700 : 600 }}>
              {title}
            </Typography>
            {accentBadge}
          </Box>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Switch
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          size={isMaster ? 'medium' : 'small'}
        />
      </Box>
      {children}
    </Paper>
  );
};

// =============================================================
// ラジオグループ（N択・排他）。各選択肢を「選択カード行」にし、
// 選択中の行だけにトグルカードと同じ白系強調を適用する。
// =============================================================
export interface SettingRadioOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface SettingRadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  options: SettingRadioOption[];
  name?: string;
  disabled?: boolean;
}

export const SettingRadioGroup: React.FC<SettingRadioGroupProps> = ({
  value, onChange, options, name, disabled,
}) => (
  <RadioGroup name={name} value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <Paper key={opt.value} elevation={0} variant="outlined" sx={surfaceSx(selected, 'default', 1)}>
          <FormControlLabel
            value={opt.value}
            disabled={disabled}
            control={<Radio sx={{ ml: 0.5 }} />}
            sx={{ m: 0, width: '100%', py: 1, pr: 1.5, alignItems: 'center' }}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {opt.icon && <IconBadge checked={selected} size={34}>{opt.icon}</IconBadge>}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                    {opt.label}
                  </Typography>
                  {opt.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                      {opt.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            }
          />
        </Paper>
      );
    })}
  </RadioGroup>
);
